import { useState, useEffect } from 'react'
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts'
import { analyticsService } from '@/services/analyticsService'
import { AnalyticsData, GeographicData, TimeSeriesData } from '@/types'
import { TrendingUp, Eye, Download, Share, Globe, Users, Activity } from 'lucide-react'

interface AnalyticsDashboardProps {
  contentId?: string
  tenantId?: string
  isAdmin?: boolean
}

// Use theme colors for consistency
const COLORS = [
  'var(--stellar-blue)',
  'var(--stellar-cyan)', 
  'var(--stellar-green)',
  'var(--stellar-gold)',
  'var(--stellar-purple)'
]

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
  contentId,
  tenantId,
  isAdmin = false
}) => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [timeRange, setTimeRange] = useState('7d')

  useEffect(() => {
    loadAnalytics()
  }, [contentId, tenantId, timeRange])

  const loadAnalytics = async () => {
    setLoading(true)
    try {
      let data: AnalyticsData
      
      if (isAdmin) {
        data = await analyticsService.getPlatformAnalytics(timeRange)
      } else if (contentId) {
        data = await analyticsService.getContentAnalytics(contentId, timeRange)
      } else if (tenantId) {
        data = await analyticsService.getTenantAnalytics(tenantId, timeRange)
      } else {
        throw new Error('No analytics context provided')
      }
      
      setAnalytics(data)
    } catch (error) {
      console.error('Failed to load analytics:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-stellar-blue"></div>
      </div>
    )
  }

  if (!analytics) {
    return (
      <div className="text-center py-8">
        <p className="text-stellar-cyan">No analytics data available</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-white">Analytics Dashboard</h2>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="px-3 py-2 border border-stellar-blue/30 rounded-md focus:outline-none focus:ring-2 focus:ring-stellar-blue bg-cosmic-dark text-white"
        >
          <option value="24h">Last 24 Hours</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
        </select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="cosmic-glass p-6 rounded-lg">
          <div className="flex items-center">
            <div className="p-2 bg-stellar-blue/20 rounded-lg">
              <Eye className="h-6 w-6 text-stellar-blue" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-stellar-cyan">Total Views</p>
              <p className="text-2xl font-bold text-white">{analytics.views.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="cosmic-glass p-6 rounded-lg">
          <div className="flex items-center">
            <div className="p-2 bg-stellar-green/20 rounded-lg">
              <Download className="h-6 w-6 text-stellar-green" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-stellar-cyan">Downloads</p>
              <p className="text-2xl font-bold text-white">{analytics.downloads.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="cosmic-glass p-6 rounded-lg">
          <div className="flex items-center">
            <div className="p-2 bg-stellar-purple/20 rounded-lg">
              <Share className="h-6 w-6 text-stellar-purple" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-stellar-cyan">Shares</p>
              <p className="text-2xl font-bold text-white">{analytics.shares.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="cosmic-glass p-6 rounded-lg">
          <div className="flex items-center">
            <div className="p-2 bg-stellar-gold/20 rounded-lg">
              <TrendingUp className="h-6 w-6 text-stellar-gold" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-stellar-cyan">Revenue</p>
              <p className="text-2xl font-bold text-white">${analytics.revenue.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Time Series Chart */}
        <div className="cosmic-glass p-6 rounded-lg">
          <h3 className="text-lg font-medium text-white mb-4">Activity Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics.timeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="views" stroke="var(--stellar-blue)" strokeWidth={2} />
              <Line type="monotone" dataKey="downloads" stroke="var(--stellar-cyan)" strokeWidth={2} />
              <Line type="monotone" dataKey="revenue" stroke="var(--stellar-gold)" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Geographic Distribution */}
        <div className="cosmic-glass p-6 rounded-lg">
          <h3 className="text-lg font-medium text-white mb-4">Geographic Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={analytics.geographicData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ country, count }) => `${country}: ${count}`}
                outerRadius={80}
                fill="var(--stellar-purple)"
                dataKey="count"
              >
                {analytics.geographicData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Geographic Data Table */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-white mb-4">Top Countries</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-stellar-blue/20">
            <thead className="bg-cosmic-dark/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-stellar-cyan uppercase tracking-wider">
                  Country
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stellar-cyan uppercase tracking-wider">
                  Views
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-stellar-cyan uppercase tracking-wider">
                  Revenue
                </th>
              </tr>
            </thead>
            <tbody className="bg-cosmic-dark/30 divide-y divide-stellar-blue/20">
              {analytics.geographicData.map((data, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                    {data.country}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-stellar-cyan">
                    {data.count.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-stellar-cyan">
                    ${data.revenue.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default AnalyticsDashboard 